import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle2, Disc3, Globe2, Scale } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";

const IMG =
  "https://images.unsplash.com/photo-1642177192159-f76c78e07690?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODF8MHwxfHNlYXJjaHwzfHxtdXNpYyUyMHN0dWRpbyUyMG5lb24lMjBsaWdodHMlMjBhbGJ1bSUyMGNvdmVyJTIwYXJ0aXN0JTIwcmVjb3JkaW5nJTIwbWljcm9waG9uZSUyMHN5bnRofGVufDB8fHx8MTc5MTUxOTQ5NHww&ixlib=rb-4.1.0&q=85";

export const AlbumHero = () => {
  const { t } = useLanguage();
  return (
      <section className="mx-auto max-w-6xl px-4 py-16 sm:px-6 lg:px-8 lg:py-24">
        <div className="grid items-center gap-12 lg:grid-cols-2">
          <div className="fade-up">
            <p className="eyebrow">{t("Album Production")}</p>
            <h1 className="mt-4 font-display text-3xl font-extrabold tracking-tight text-slate-50 sm:text-4xl lg:text-5xl">
              {t("Your album, from demo to distribution")}
            </h1>
            <p className="mt-5 max-w-lg text-base leading-relaxed text-slate-400">
              {t("Full album productions with arrangement, recording, mixing and mastering — plus clear options for rights, royalties and worldwide distribution.")}
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <Link to="/order?package=album" data-testid="album-hero-order-cta" className="btn-primary">
                {t("Start Your Album")} <ArrowRight size={16} />
              </Link>
              <Link to="/contact" data-testid="album-hero-contact-cta" className="btn-ghost">
                <Disc3 size={16} /> {t("Book a Producer Consultation")}
              </Link>
            </div>
          </div>
          <div className="studio-card fade-up overflow-hidden" style={{ animationDelay: "0.15s" }}>
            <img src={IMG} alt={t("Synthesizer keyboard in dark studio light during album production")} className="h-72 w-full object-cover sm:h-96" />
          </div>
        </div>
      </section>
  );
};

export const AlbumFeatures = () => {
  const { t } = useLanguage();
  return (
      <section className="border-y border-[#282C3D] bg-[#0E1018]">
        <div className="mx-auto max-w-6xl px-4 py-20 sm:px-6 lg:px-8">
          <p className="eyebrow">{t("Scope")}</p>
          <h2 className="mt-3 font-display text-2xl font-bold text-slate-100 sm:text-3xl">{t("Everything a record needs")}</h2>
          <div className="mt-10 grid gap-6 md:grid-cols-3">
            {[
              { icon: Disc3, title: "Production", text: "Multi-track arrangement, recording, mixing and mastering — with session musicians and full studio orchestra on request." },
              { icon: Scale, title: "Rights & Royalties", text: "Flexible options for master rights and royalty distribution. You decide how much of your record you keep." },
              { icon: Globe2, title: "Distribution", text: "Release setup on Spotify, Apple Music and all major platforms — including a vinyl pressing option." },
            ].map((c, i) => (
              <div key={c.title} data-testid={`album-feature-${i + 1}`} className="studio-card fade-up p-6" style={{ animationDelay: `${i * 0.08}s` }}>
                <c.icon className="text-[#E5A93C]" size={22} />
                <h3 className="mt-4 font-display text-lg font-semibold text-slate-100">{t(c.title)}</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-400">{t(c.text)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
  );
};

export const AlbumQuote = () => {
  const { t } = useLanguage();
  return (
      <section className="mx-auto max-w-6xl px-4 py-20 sm:px-6 lg:px-8">
        <div className="studio-card flex flex-col items-start justify-between gap-8 p-8 sm:p-12 lg:flex-row lg:items-center">
          <div className="max-w-xl">
            <p className="eyebrow">{t("Pricing")}</p>
            <h2 className="mt-3 font-display text-2xl font-bold text-slate-50 sm:text-3xl">{t("By agreement — scaled to your record")}</h2>
            <p className="mt-3 text-sm leading-relaxed text-slate-400">
              {t("Number of tracks, musicians, studio time, rights and distribution scope all shape the budget. In a free consultation we define the production plan and you receive a written offer.")}
            </p>
            <ul className="mt-5 space-y-2.5 text-sm text-slate-300">
              {["Free producer consultation", "Written offer with milestones", "Rights & distribution tailored to you"].map((f) => (
                <li key={f} className="flex gap-2.5">
                  <CheckCircle2 size={16} className="mt-0.5 shrink-0 text-[#10B981]" />
                  {t(f)}
                </li>
              ))}
            </ul>
          </div>
          <Link to="/contact" data-testid="album-consultation-cta" className="btn-primary shrink-0">
            {t("Book a Consultation")} <ArrowRight size={16} />
          </Link>
        </div>
      </section>
  );
};

export default function Album() {
  return <div><AlbumHero /><AlbumFeatures /><AlbumQuote /></div>;
}
